import { lazy } from "react";

const Dashboard = lazy(() => import("@pages/Dashboard"));
const Analytics = lazy(
  () => import("@pages/Analytics/AnalyticsDashboard/AnalyticsDasboard")
);
const RecipientsEmails = lazy(
  () => import("@pages/Analytics/RecipientsEmails")
);
const AnalyticsInsights = lazy(
  () => import("@pages/Analytics/Insights")
);
const MarketingPopups = lazy(() => import("@pages/MarketingPopups"));
const BrandingMarketing = lazy(() => import("@pages/WebsiteCMS/BrandingMarketing"));
const UserSettings = lazy(() => import("@pages/UserProfile/userSettings"));
const UserBooking = lazy(() => import("@pages/UserProfile/UserBookings"));
const UserLanguage = lazy(() => import("@pages/UserProfile/UserLanguage"));
const Pricing = lazy(() => import("@pages/UserProfile/Pricing"));
const DynamicRules = lazy(
  () => import("@pages/UserProfile/Pricing/DynamicRules")
);
const BookingListView = lazy(() => import("@pages/Bookings/Booking"));
const OperationView = lazy(() => import("@pages/Bookings/OperationHours2"));
const WaillistView = lazy(() => import("@pages/Bookings/Waitlist"));
const CancellationView = lazy(() => import("@pages/Bookings/Cancellations"));
const AllCustomers = lazy(() => import("@pages/Customers/CustomerList"));
const MembersCustomer = lazy(() => import("@pages/Customers/CustomerMembers"));
const Members = lazy(() => import("@pages/Memberships/CustomerMembers"));
const StandardCustomer = lazy(() => import("@pages/Customers/non-members"));
const MembershipPlans = lazy(
  () => import("@pages/Memberships/PlansMembership")
);

const CalendarView = lazy(
  () => import("@pages/Bookings/Booking/BookingCalendar/BookingCalendarView")
);

const TransactionList = lazy(
  () => import("@pages/Transaction/TransactionList")
);
const PosPage = lazy(() => import("@pages/POS/index"));
const LeagueManagerPage = lazy(() => import("@pages/LeagueManager/index"));
const SocialMinerPage = lazy(() => import("@pages/SocialMiner/index"));
const MarketingEnginePage = lazy(() => import("@pages/SocialMiner/index"));
const ReportingPage = lazy(() => import("@pages/Reporting/index"));
const LoginPage = lazy(() => import("@pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@pages/auth/RegisterPage"));
const VerificationOtpPage = lazy(
  () => import("@pages/auth/VerificationOtpPage")
);
const ConfirmationPage = lazy(() => import("@pages/auth/ConfirmationPage"));
const ResetPassword = lazy(() => import("@pages/auth/ResetPassword"));
const PasswordVerification = lazy(
  () => import("@pages/auth/PasswordVerification")
);
const NewPassword = lazy(() => import("@pages/auth/NewPassword"));
const UserInvitePage = lazy(() => import("@pages/auth/UserInvitePage"));
const GoogleCallback = lazy(() => import("@pages/auth/GoogleCallback"));
const Errorpage = lazy(() => import("@pages/ErrorPage"));

const LeaguesEventsCalendarView = lazy(
  () => import("@pages/LeaguesEvents/CalendarEvents/LeagueCalendarView")
);
const EventContactView = lazy(
  () => import("@pages/LeaguesEvents/EventContact/index")
);
// const Subscribers = lazy(
//   () => import("@pages/LeaguesEvents/Subscribers/index")
// );
const LeagueEventsList = lazy(
  () => import("@pages/LeaguesEvents/LeagueEventsList/LeagueEventsList")
);

const Venue = lazy(() => import("@pages/UserProfile/Venue"));
const Notifications = lazy(() => import("@pages/UserProfile/Notifications"));
const Contactforms = lazy(() => import("@pages/inbox/ContactForm/index"));
const Newsletter = lazy(() => import("@pages/inbox/Newsletter/index"));
const Leagues = lazy(() => import("@pages/inbox/Leagues/index"));
const Events = lazy(() => import("@pages/inbox/Events/index"));
const BookingsMonthlyCalendarView = lazy(
  () => import("@pages/Bookings/Booking/BookingCalendar/BookingMonthlyView")
);

const Notification = lazy(
  () => import("@pages/Notification/NotificationPage/index")
);
const NotAuthorized = lazy(() => import("@pages/NotAuthorized"));

const LessonsCalendar = lazy(
  () => import("@pages/Lessons/Lessons&Fittings/CalendarView/index")
);
const LessonsList = lazy(
  () => import("@pages/Lessons/Lessons&Fittings/ListView/index")
);
const Coaches = lazy(() => import("@pages/Lessons/Coaches/index"));
const Availability = lazy(() => import("@pages/Lessons/Availability/index"));

// private routes
export const privateRoutes = [
  {
    name: "dashboard",
    path: "/dashboard",
    component: Dashboard,
    permission: { resource: "Dashboard", action: "view" },
  },
  { name: "not authorized", path: "/not-authorized", component: NotAuthorized },
  {
    name: "settings",
    path: "/settings",
    component: UserSettings,
    // No permission check - all users can access settings (Current User tab)
    // Individual tabs will show locks/view-only messages based on permissions
  },

  {
    name: "analytics",
    path: "/analytics",
    component: Analytics,
    permission: { resource: "Analytics", action: "view" },
  },
  {
    name: "insights",
    path: "/analytics/insights",
    component: AnalyticsInsights,
    permission: { resource: "Analytics", action: "view" },
  },
  {
    name: "recipients-emails",
    path: "/analytics/reports_schedule",
    component: RecipientsEmails,
    permission: { resource: "Analytics", action: "view" },
  },
  {
    name: "branding & marketing",
    path: "/website-cms/branding",
    component: BrandingMarketing,
    // TODO: Enable permission check once backend grants "Website CMS" permission to Owner/Operator
    // permission: { resource: "Website CMS", action: "view" },
  },
  {
    name: "marketing popups",
    path: "/website-cms/marketing-popups",
    component: MarketingPopups,
    // TODO: Enable permission check once backend grants "Marketing Popups" permission to Owner/Operator
    // permission: { resource: "Marketing Popups", action: "view" },
  },

  {
    name: "user bookings",
    path: "/settings/bookings",
    component: UserBooking,
    // No permission check - all users can view, restricted features show locks
  },
  {
    name: "user venue",
    path: "/settings/venue",
    component: Venue,
    // No permission check - all users can view, restricted features show locks
  },
  {
    name: "user notifications",
    path: "/settings/notifications",
    component: Notifications,
    // No permission check - all users can view, restricted features show locks
  },
  {
    name: "pricing",
    path: "/settings/pricing",
    component: Pricing,
    // No permission check - all users can view, restricted features show locks
  },
  {
    name: "dynamic-pricing",
    path: "/settings/pricing/dynamic",
    component: DynamicRules,
    permission: { resource: "Pricing Configuration", action: "view" },
  },
  {
    name: "user language",
    path: "/settings/language",
    component: UserLanguage,
    // No permission check - all users can view, restricted features show locks
  },
  {
    name: "bookings",
    path: "/bookings",
    component: BookingListView,
    permission: { resource: "Bookings", action: "view" },
  },
  {
    name: "operations",
    path: "/bookings/operation-hours",
    component: OperationView,
    permission: { resource: "Business Hours", action: "view" },
  },
  {
    name: "waitlist",
    path: "/bookings/waitlist",
    component: WaillistView,
    permission: { resource: "Bookings", action: "view" },
  },
  {
    name: "bookings calendar",
    path: "/booking/calendar",
    component: CalendarView,
    permission: { resource: "Bookings", action: "view" },
  },

  {
    name: "cancellations",
    path: "/bookings/cancellations",
    component: CancellationView,
    permission: { resource: "Bookings", action: "view" },
  },
  {
    name: "customers",
    path: "/customers",
    component: AllCustomers,
    permission: { resource: "Customer Management", action: "view" }, // API sends "Customer Management", not "Users"
  },
  {
    name: "customers members",
    path: "/customers/members",
    component: MembersCustomer,
    permission: { resource: "Customer Management", action: "view" }, // API sends "Customer Management", not "Users"
  },
  {
    name: "customers standard",
    path: "/customers/non-members",
    component: StandardCustomer,
    permission: { resource: "Customer Management", action: "view" }, // API sends "Customer Management", not "Users"
  },
  {
    name: "events calendar",
    path: "/events/calendar",
    component: LeaguesEventsCalendarView,
    permission: { resource: "Calendar Events", action: "view" }, // API sends "Calendar Events", not "Events"
  },

  {
    name: "Lessons & Fittings",
    path: "/lessons/calendar",
    component: LessonsCalendar,
    permission: { resource: "Lesson & Coach", action: "view" }, // API sends "Lesson & Coach", not "Coaching"
  },
  {
    name: "Lessons & Fittings",
    path: "/lessons/list",
    component: LessonsList,
    permission: { resource: "Lesson & Coach", action: "view" }, // API sends "Lesson & Coach", not "Coaching"
  },
  {
    name: "Availability",
    path: "/lessons/coach-availability",
    component: Availability,
    permission: { resource: "Lesson & Coach", action: "view" }, // API sends "Lesson & Coach", not "Coaching"
  },
  {
    name: "Coaches",
    path: "/lessons/coaches",
    component: Coaches,
    permission: { resource: "Lesson & Coach", action: "view" }, // API sends "Lesson & Coach", not "Coaching"
  },
  {
    name: "league players",
    path: "/events/event-contacts",
    component: EventContactView,
    permission: { resource: "Calendar Events", action: "view" }, // API sends "Calendar Events", not "Events"
  },
  // {
  //   name: "subscribers",
  //   path: "/league/subscribers",
  //   component: Subscribers,
  // },

  {
    name: "memberships plans",
    path: "/membership/plans",
    component: MembershipPlans,
    permission: { resource: "Membership Plans", action: "view" }, // API sends "Membership Plans" (plural)
  },
  {
    name: "members",
    path: "/membership/members",
    component: Members,
    permission: { resource: "Members", action: "view" }, // API sends "Members"
  },
  {
    name: "transaction",
    path: "/transaction",
    component: TransactionList,
    permission: { resource: "Transactions", action: "view" }, // API sends "Transactions" (plural)
  },
  {
    name: "Contact Form",
    path: "/inbox/contact-form",
    component: Contactforms,
    permission: {
      anyOf: [
        {
          resource: "Subscriptions",
          subResource: "contact us manage",
          action: "view",
        },
        {
          resource: "Subscriptions",
          subResource: "contact form",
          action: "view",
        },
        {
          resource: "Subscriptions",
          subResource: "contact forms",
          action: "view",
        },
        { resource: "Subscriptions", action: "view" },
      ],
    },
  },
  {
    name: "Newsletter",
    path: "/inbox/newsletter",
    component: Newsletter,
    permission: {
      anyOf: [
        {
          resource: "Subscriptions",
          subResource: "newsletter contact form",
          action: "view",
        },
        {
          resource: "Subscriptions",
          subResource: "newsletter",
          action: "view",
        },
        { resource: "Subscriptions", action: "view" },
      ],
    },
  },
  {
    name: "Leagues",
    path: "/subscriptions/leagues",
    component: Leagues,
    permission: {
      anyOf: [
        {
          resource: "Subscriptions",
          subResource: "event subscription",
          action: "view",
        },
        {
          resource: "Subscriptions",
          subResource: "newsletter contact form",
          action: "view",
        },
        {
          resource: "Subscriptions",
          subResource: "contact us manage",
          action: "view",
        },
      ],
    },
  },
  {
    name: "Events",
    path: "/subscriptions/events",
    component: Events,
    permission: {
      resource: "Subscriptions",
      subResource: "event subscription",
      action: "view",
    },
  },
  { name: "Leagues", path: "/inbox/leagues", component: Leagues },
  { name: "Events", path: "/inbox/events", component: Events },

  { name: "pos", path: "/pos", component: PosPage },
  {
    name: "league manager",
    path: "/league-manager",
    component: LeagueManagerPage,
  },
  {
    name: "League Events",
    path: "/league-events",
    component: LeagueEventsList,
    // TODO: Backend needs to provide "Leagues" permission, or confirm this should use "Calendar Events"
    // Temporarily removed permission check to allow access
    // permission: { resource: "Leagues", action: "view" },
  },

  { name: "social miner", path: "/social-miner", component: SocialMinerPage },
  {
    name: "marketing engine",
    path: "/marketing-engine",
    component: MarketingEnginePage,
  },
  { name: "reporting", path: "/reporting", component: ReportingPage },
  {
    name: "bookings monthly calendar",
    path: "/bookings/monthly",
    component: BookingsMonthlyCalendarView,
  },
  { name: "notification", path: "/notification", component: Notification },
];

// public routes
export const publicRoutes = [
  { name: "Login", path: "/login", component: LoginPage },
  { name: "Register", path: "/register", component: RegisterPage },
  {
    name: "verification",
    path: "/verification",
    component: VerificationOtpPage,
  },
  { name: "confirmation", path: "/confirmation", component: ConfirmationPage },
  { name: "Reset Password", path: "/reset-password", component: ResetPassword },
  {
    name: "Password Verification",
    path: "/password-verification",
    component: PasswordVerification,
  },
  { name: "New Password", path: "/change-password/", component: NewPassword },
  { name: "User Invite", path: "/user-invite/", component: UserInvitePage },
  { name: "Google Callback", path: "/auth/google/callback", component: GoogleCallback },
  { name: "Error Page", path: "*", component: Errorpage },
];
